"use client";

import { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import { cn, formatIsoStringDate } from "@/lib/utils";
import { t } from "@/lib/localization";

import { GetFilesResponseType } from "../../hooks/useGetFiles";
import { useGetLomda } from "@/features/storage/sheets/lomdaSheet/hooks/useGetLomda";

import LomdaSceneLayout from "@/features/lomdaEditor/components/layout/lomdaSceneLayout";
import { LomdaContent, LomdaMetadata } from "@/lib/utils/lomda/types";

interface LomdaCardProps {
    file: GetFilesResponseType["data"][0];
    onClick?: () => void;
    options?: {
        component: ReactNode;
        positionClassName?: string;
    };
    selected?: boolean;
}

export default function LomdaCard({
    file,
    onClick,
    options,
    selected,
}: LomdaCardProps) {
    const { data: lomdaData } = useGetLomda(true, file.id);

    const lomda = lomdaData?.data;

    const content: LomdaContent | null = lomda?.content
        ? JSON.parse(lomda.content)
        : null;

    const metadata: LomdaMetadata | null = lomda?.metadata
        ? JSON.parse(lomda.metadata)
        : null;

    const badgeText =
        file.detailForBadge <= 1
            ? t("storage.lomda-card.num-of-scenes-badge.single")
            : file.detailForBadge +
              " " +
              t("storage.lomda-card.num-of-scenes-badge.multiple");

    const title = file.name || t("storage.no-name", undefined, undefined);

    const subtitle = `${t("storage.lomda", undefined, undefined)} · ${formatIsoStringDate(file.date)}`;

    const hasScene = content?.scenes?.length;

    return (
        <Card
            onClick={onClick}
            className={cn(
                "relative group cursor-pointer hover:bg-accent transition shadow-md flex flex-col",
                selected && "border-primary"
            )}
        >
            {options && (
                <div
                    className={cn(
                        "absolute z-10 flex",
                        options.positionClassName || "top-2 left-2"
                    )}
                >
                    {options.component}
                </div>
            )}

            <div className="relative aspect-video rounded-t-md border-b overflow-hidden bg-muted">
                {hasScene ? (
                    <LomdaSceneLayout
                        scene={content!.scenes[0]}
                        logoUrl={metadata?.logoUrl}
                        thumbnail
                        className="border-none"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        {t("storage.no-thumbnail", undefined, undefined)}
                    </div>
                )}

                <Badge className="absolute bottom-2 left-2 font-normal px-2">
                    {badgeText}
                </Badge>
            </div>

            <footer className="flex flex-row gap-2 px-3 py-4">
                <div className="flex flex-col truncate gap-2">
                    <h3 className="font-medium truncate" title={title}>
                        {title}
                    </h3>

                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                </div>
            </footer>
        </Card>
    );
}
